import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { API } from "@/context/AuthContext";

// Correct gated quantities while no GRN exists; the note is mandatory and becomes part of the audit trail.
export function GateInEditDialog({ entry, onSaved }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setQty(Object.fromEntries(entry.lines.map((l) => [l.item, String(l.qty_received)]))); setNote(""); setError(""); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    const lines = entry.lines.map((l) => ({ item: l.item, qty_received: Number(qty[l.item]) }));
    if (lines.some((l) => !(l.qty_received > 0))) return setError("Every quantity must be greater than 0");
    if (note.trim().length < 3) return setError("Please enter a short reason for the correction");
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/inventory/gate-in-register/${entry.id}`, { lines, note: note.trim() });
      toast.success(`Gate-in corrected (${r.data.changes.length} line${r.data.changes.length > 1 ? "s" : ""}) — reprint the labels`);
      setOpen(false);
      onSaved?.();
    } catch (err) { setError(err?.response?.data?.detail || "Could not save the correction"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-amber-500 hover:text-white hover:border-amber-500" title="Correct the gated quantity (no GRN yet)" data-testid={`register-edit-${entry.id}`}>
          <Pencil className="h-3 w-3" />Edit
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="gatein-edit-dialog">
        <DialogHeader>
          <DialogTitle>Correct gate-in quantity</DialogTitle>
          <DialogDescription>PO {entry.po} · gated {entry.gate_in_date}. Allowed only while no GRN has been posted. Changes are recorded with your name, time and note.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2">
            {entry.lines.map((l) => (
              <div key={l.item} className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-mono font-semibold text-slate-900 truncate">Line {l.item} · {l.material}</p>
                  <p className="text-xs text-slate-500 truncate">{l.description} · gated {l.qty_received} {l.unit}</p>
                </div>
                <Input type="number" min="0" step="any" value={qty[l.item] ?? ""} onChange={(e) => setQty((q) => ({ ...q, [l.item]: e.target.value }))} className="h-9 w-28 text-right tabular-nums" data-testid={`gatein-edit-qty-${l.item}`} />
                <span className="text-xs text-slate-500 w-10">{l.unit}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gi-note">Reason for correction <span className="text-red-600">*</span></Label>
            <Input id="gi-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 2 cartons short-shipped, counted again at gate" data-testid="gatein-edit-note" />
          </div>
          {entry.edits?.length > 0 && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-1" data-testid="gatein-edit-history">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1"><History className="h-3 w-3" />Previous corrections</p>
              {entry.edits.map((ed, i) => (
                <p key={i} className="text-xs text-slate-600">{ed.at.slice(0, 16).replace("T", " ")} · {ed.by} — {ed.changes.map((c) => `line ${c.item}: ${c.from} → ${c.to}`).join(", ")} · <i>{ed.note}</i></p>
              ))}
            </div>
          )}
          {error && <p className="text-sm text-red-600" data-testid="gatein-edit-error">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="gatein-edit-save">{busy ? "Saving…" : "Save correction"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
